'use client';

import favoriteIconActive from '@/assets/images/icons/icon-favorite-active.png';
import favoriteIcon from '@/assets/images/icons/icon-favorite.png';
import SignInPromptModal from '@/components/layouts/SignInPromptModal/SignInPromptModal';
import RoundIcon from '@/components/ui/RoundIcon/RoundIcon';
import RoundTextIcon from '@/components/ui/RoundTextIcon/RoundTextIcon';
import type { MaterialCategoryType } from '@/constants';
import type { GfMaterialItem } from '@/features/gfWorks/types';
import { swal } from '@/lib/sweetalert';
import { BadgeStatus } from '@/types';
import { pickErrorMessages } from '@/utils/functions';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { deleteFavoriteMaterial } from '../../api/deleteFavoriteMaterial';
import { postFavoriteMaterial } from '../../api/postFavoriteMaterial';
import styles from './MaterialCardList.module.scss';

function MaterialCardList({
  materials,
  onMaterialClick,
  selectedProductId,
  bgColor = 'gray100',
  cardSize = 'default',
  materialNameDisplay = 'none',
  toggleFavoriteState,
}: {
  materials: GfMaterialItem[];
  onMaterialClick?: (materialId: number) => void;
  selectedProductId?: number;
  bgColor?: string;
  cardSize?: 'default' | 'medium';
  // none: 素材名を表示しない / below: サムネイルの下に素材名を表示 / overlay: サムネイル画像の上に重ねて素材名を表示
  materialNameDisplay?: 'none' | 'below' | 'overlay';
  toggleFavoriteState: (materialId: number, materialCategoryName: MaterialCategoryType) => void; //APIのロードなしで親の状態変数を変えたい
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [signInPromptModalOpen, setSignInPromptModalOpen] = useState(false);
  const router = useRouter();
  const session = useSession();
  const toggleFavorite = async (
    id: number,
    materialCategoryName: MaterialCategoryType,
    selectedMaterialIsFavorite: boolean,
  ) => {
    try {
      setIsLoading(true);
      if (!session.data) {
        setSignInPromptModalOpen(true);
        return;
      }
      if (selectedMaterialIsFavorite) {
        await deleteFavoriteMaterial({ gfMaterialId: id });
      } else {
        await postFavoriteMaterial({ gfMaterialId: id });
      }
      toggleFavoriteState(id, materialCategoryName);
      router.refresh();
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`${styles.cardContainer} ${styles[`${cardSize}CardContainer`]}`}>
      {materials.length > 0 &&
        materials.map((material, index) => (
          <div
            className={`${styles.cardWrapper} ${styles[`${cardSize}CardWrapperSize`]}`}
            key={material.id}
            onClick={() => onMaterialClick?.(material.id)}
          >
            {/* 素材サムネイル */}
            <div
              className={`${styles.thumbnailContainer} ${styles[`${cardSize}ThumbnailContainerSize`]} ${
                materialNameDisplay === 'overlay' ? styles.thumbnailContainerWithLabel : ''
              }`}
              style={{
                backgroundColor: `var(--color-${bgColor})`,
              }}
              data-testid='material-thumbnail'
            >
              <div className={`${styles.thumbnail} ${styles[`${cardSize}ThumbnailSize`]}`}>
                <Image
                  src={material.thumbnailPath}
                  alt={material.name}
                  fill
                  sizes={
                    cardSize === 'default'
                      ? getImageLayoutSizes({ sp: '33vw', pc: '170px' })
                      : getImageLayoutSizes({ sp: '50vw', pc: '200px' })
                  }
                  className={styles.thumbnailImg}
                  priority={index < 3}
                />
              </div>
              {/* バッジステータスがある場合に表示 */}
              {material.badgeStatus === BadgeStatus.new.value && (
                <div className={styles.badgeStatus}>
                  <RoundTextIcon bgColor='orange' text={BadgeStatus[material.badgeStatus].text} borderColor='gray100' />
                </div>
              )}
              {/* お気に入りボタン - 商品IDがない場合のみ表示 */}
              {!selectedProductId && (
                <button
                  className={`${styles.favoriteButton} ${material.isFavorite ? styles.active : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(material.id, material.materialType, material.isFavorite);
                  }}
                  type='button'
                  disabled={isLoading}
                  data-testid={`favorite-button-${material.id}`}
                >
                  <RoundIcon
                    imgSrc={material.isFavorite ? favoriteIconActive.src : favoriteIcon.src}
                    size='small'
                    bgColor='white'
                    borderColor='gray100'
                  />
                </button>
              )}
              {materialNameDisplay === 'overlay' && <div className={styles.materialNameOverlay}>{material.name}</div>}
            </div>
            {materialNameDisplay === 'below' && (
              <div className={styles.materialNameContainer}>
                <div className={styles.materialName}>{material.name}</div>
              </div>
            )}
          </div>
        ))}
      <SignInPromptModal
        isOpen={signInPromptModalOpen}
        onClose={() => setSignInPromptModalOpen(false)}
        description='コマをお気に入りに登録するには、ログインが必要です。'
      />
    </div>
  );
}

export default MaterialCardList;
